// 📄 src/app/routes/guards/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { TOKEN_KEY } from "../../../api/axiosClient";
import { requestUpgrade } from "../../../utils/upgradeBus";
import { requiresBusinessSession } from "../../../utils/businessScopePaths";

import { PROFILE_COMPLETION_PATH, withLocation } from "../navigationPaths";

const AUTH_LOADING_TIMEOUT_MS = 10000;

export default function ProtectedRoute({ children, featureCode }) {
  const location = useLocation();
  const {
    isLoading,
    user,
    business,
    role,
    roleKey,
    roleScope,
    status, // e.g., "active" | "profilepending" | "suspended"
    hasFeature,
    hasAllAccess,
    isPlatformSuperAdmin,
    refreshAuthContext,
    logout,
  } = useAuth() || {};
  const [authLoadingTimedOut, setAuthLoadingTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setAuthLoadingTimedOut(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setAuthLoadingTimedOut(true);
      // eslint-disable-next-line no-console
      console.warn("[Auth] ProtectedRoute auth loading exceeded timeout.");
    }, AUTH_LOADING_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [isLoading]);

  const handleRetryAuthLoad = async () => {
    setAuthLoadingTimedOut(false);
    if (typeof refreshAuthContext === "function") {
      try {
        await refreshAuthContext();
        return;
      } catch {
        // Fall through to a page reload if the provider refresh path fails.
      }
    }

    window.location.reload();
  };

  const handleGoToLogin = async () => {
    if (typeof logout === "function") {
      await logout({
        redirectTo: "/login",
        silent: true,
        toastMessage: null,
      });
      return;
    }

    window.location.assign("/login");
  };

  const searchParams = new URLSearchParams(location?.search || "");
  const isPaymentStatusReturn =
    location?.pathname === "/app/payment/status" &&
    (searchParams.has("checkoutSessionId") ||
      searchParams.get("source") === "wallet");

  // 0) If there is no token at all, go to login immediately.
  const token =
    typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (!token) {
    return <Navigate to={withLocation("/login", location)} replace />;
  }

  // 1) While auth is still loading, avoid premature redirects. If loading
  // stalls, show a small recovery UI instead of leaving a blank route.
  if (isLoading) {
    if (!authLoadingTimedOut) {
      return null;
    }

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h1 className="text-base font-semibold text-slate-900">
            We are still loading your session
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Please retry. If this keeps happening, return to login and sign in again.
          </p>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={handleRetryAuthLoad}
              className="inline-flex flex-1 items-center justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={handleGoToLogin}
              className="inline-flex flex-1 items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 2) Handle onboarding / account status.
  const s = String(status || "").toLowerCase();
  if (s === "suspended" || s === "blocked") {
    return <Navigate to={withLocation("/no-access", location)} replace />;
  }

  // 3) Enforce "authenticated" presence minimally.
  // User must exist; business may be missing for admin-ish roles.
  const safeRole = String(role || "").toLowerCase();
  const isPlatformActor =
    String(roleScope || "").toLowerCase() === "platform" ||
    [
      "platform.super_admin",
      "platform.partner",
      "platform.reseller",
    ].includes(String(roleKey || "").toLowerCase()) ||
    ["superadmin", "admin", "partner", "reseller"].includes(safeRole);

  // business can be { id } or { businessId }
  const hasBusinessId = !!(business && (business.id || business.businessId));

  if (!user) {
    // No user after loading (token invalid/expired server-side)
    return <Navigate to={withLocation("/login", location)} replace />;
  }

  // Allow authenticated checkout-return traffic to always access payment status.
  if (isPaymentStatusReturn && s !== "profilepending") {
    return children;
  }

  // For business/staff roles we still expect a business; for admin-ish we don't.
  if (!isPlatformActor && !hasBusinessId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-lg border bg-white p-5">
          <h1 className="font-semibold text-slate-900">Your business context is unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">Retry loading your session or sign in again to recover your workspace.</p>
          <div className="mt-5 flex gap-4">
            <button type="button" className="text-emerald-700 underline" onClick={handleRetryAuthLoad}>Retry</button>
            <button type="button" className="text-emerald-700 underline" onClick={handleGoToLogin}>Go to login</button>
          </div>
        </div>
      </div>
    );
  }

  // The canonical page still runs its own profile-update permission guard.
  if (s === "profilepending" && location.pathname.replace(/\/$/, "") !== PROFILE_COMPLETION_PATH) {
    return <Navigate to={withLocation(PROFILE_COMPLETION_PATH, location)} replace />;
  }

  // Old actor-pricing bookmarks must use the administrative pricing page.
  if (isPlatformSuperAdmin && /^\/app\/(partner|reseller)\/pricing\/?$/.test(location.pathname)) {
    return <Navigate to="/app/admin/pricing" replace />;
  }

  // Stop business pages (and their requests) before mounting the app layout.
  // Impersonation has a business role and follows the normal member path.
  if (isPlatformSuperAdmin && (
    location.pathname.replace(/\/$/, "") === "/app" ||
    requiresBusinessSession(location.pathname)
  )) {
    return <Navigate to="/app/admin/approvals" replace />;
  }

  // 4) Optional feature-gating per route.
  if (featureCode && !hasAllAccess) {
    if (typeof hasFeature === "function" && !hasFeature(featureCode)) {
      requestUpgrade({ reason: "feature", code: featureCode });
      // Use your in-app billing route (matches axiosClient’s interceptors)
      return <Navigate to={withLocation(
        location.pathname === "/app/settings/billing" ? "/no-access" : "/app/settings/billing",
        location
      )} replace />;
    }
  }

  // 5) All checks passed
  return children;
}
